// Nexus Dex master data, species roster and visual helpers.
//
// LEGAL: This file used to fetch sprite art from PokeAPI's GitHub mirror,
// which serves Game Freak / Nintendo creative work. That is now removed.
// All visual representations of species in production come from either:
//   1. The Nexus Dex glyph system (lib/glyph.ts), original artwork, OR
//   2. The user's own screenshots ingested through Box Scan.
//
// The legacy `spriteUrl()` helper now returns a glyph data URI so any older
// call sites stay safe by default. New code should import NexusGlyph from
// components/NexusGlyph.tsx for crisp inline SVG rendering.

import type { DexStatus } from "./data";
import { nexusGlyphDataUri } from "./glyph";

export interface Pokemon {
  id: number;
  name: string;
  gen: number;
  // Optional flags used by Trade tab filters / Lucky-dex realism.
  inGo?: boolean;        // false = locked (not in Pokémon GO yet)
  mythical?: boolean;
  legendary?: boolean;
  ultraBeast?: boolean;
  // Optional human-readable estimate for when a locked Pokémon is expected
  // to land in Pokémon GO. Shown under the dex number with a small padlock.
  expectedRelease?: string;
}

// A curated mix across all 9 generations, real Pokédex numbers + names.
// ~120 entries chosen to feel like a believable trainer's dex sample.
export const POKEMON: Pokemon[] = [
  // Gen 1
  { id: 1,   name: "Bulbasaur",  gen: 1, inGo: true },
  { id: 4,   name: "Charmander", gen: 1, inGo: true },
  { id: 6,   name: "Charizard",  gen: 1, inGo: true },
  { id: 7,   name: "Squirtle",   gen: 1, inGo: true },
  { id: 25,  name: "Pikachu",    gen: 1, inGo: true },
  { id: 26,  name: "Raichu",     gen: 1, inGo: true },
  { id: 39,  name: "Jigglypuff", gen: 1, inGo: true },
  { id: 52,  name: "Meowth",     gen: 1, inGo: true },
  { id: 65,  name: "Alakazam",   gen: 1, inGo: true },
  { id: 94,  name: "Gengar",     gen: 1, inGo: true },
  { id: 130, name: "Gyarados",   gen: 1, inGo: true },
  { id: 131, name: "Lapras",     gen: 1, inGo: true },
  { id: 133, name: "Eevee",      gen: 1, inGo: true },
  { id: 143, name: "Snorlax",    gen: 1, inGo: true },
  { id: 144, name: "Articuno",   gen: 1, inGo: true, legendary: true },
  { id: 145, name: "Zapdos",     gen: 1, inGo: true, legendary: true },
  { id: 146, name: "Moltres",    gen: 1, inGo: true, legendary: true },
  { id: 149, name: "Dragonite",  gen: 1, inGo: true },
  { id: 150, name: "Mewtwo",     gen: 1, inGo: true, legendary: true },
  { id: 151, name: "Mew",        gen: 1, inGo: true, mythical: true },

  // Gen 2
  { id: 152, name: "Chikorita",  gen: 2, inGo: true },
  { id: 155, name: "Cyndaquil",  gen: 2, inGo: true },
  { id: 158, name: "Totodile",   gen: 2, inGo: true },
  { id: 175, name: "Togepi",     gen: 2, inGo: true },
  { id: 196, name: "Espeon",     gen: 2, inGo: true },
  { id: 197, name: "Umbreon",    gen: 2, inGo: true },
  { id: 212, name: "Scizor",     gen: 2, inGo: true },
  { id: 245, name: "Suicune",    gen: 2, inGo: true, legendary: true },
  { id: 248, name: "Tyranitar",  gen: 2, inGo: true },
  { id: 249, name: "Lugia",      gen: 2, inGo: true, legendary: true },
  { id: 250, name: "Ho-Oh",      gen: 2, inGo: true, legendary: true },
  { id: 251, name: "Celebi",     gen: 2, inGo: true, mythical: true },

  // Gen 3
  { id: 252, name: "Treecko",    gen: 3, inGo: true },
  { id: 255, name: "Torchic",    gen: 3, inGo: true },
  { id: 258, name: "Mudkip",     gen: 3, inGo: true },
  { id: 280, name: "Ralts",      gen: 3, inGo: true },
  { id: 282, name: "Gardevoir",  gen: 3, inGo: true },
  { id: 302, name: "Sableye",    gen: 3, inGo: true },
  { id: 359, name: "Absol",      gen: 3, inGo: true },
  { id: 380, name: "Latias",     gen: 3, inGo: true, legendary: true },
  { id: 381, name: "Latios",     gen: 3, inGo: true, legendary: true },
  { id: 384, name: "Rayquaza",   gen: 3, inGo: true, legendary: true },
  { id: 385, name: "Jirachi",    gen: 3, inGo: true, mythical: true },
  { id: 386, name: "Deoxys",     gen: 3, inGo: true, mythical: true },

  // Gen 4
  { id: 387, name: "Turtwig",    gen: 4, inGo: true },
  { id: 390, name: "Chimchar",   gen: 4, inGo: true },
  { id: 393, name: "Piplup",     gen: 4, inGo: true },
  { id: 445, name: "Garchomp",   gen: 4, inGo: true },
  { id: 448, name: "Lucario",    gen: 4, inGo: true },
  { id: 482, name: "Azelf",      gen: 4, inGo: true, legendary: true },
  { id: 483, name: "Dialga",     gen: 4, inGo: true, legendary: true },
  { id: 484, name: "Palkia",     gen: 4, inGo: true, legendary: true },
  { id: 487, name: "Giratina",   gen: 4, inGo: true, legendary: true },
  { id: 489, name: "Phione",     gen: 4, inGo: false, mythical: true, expectedRelease: "No date yet" },
  { id: 490, name: "Manaphy",    gen: 4, inGo: true, mythical: true },
  { id: 491, name: "Darkrai",    gen: 4, inGo: true, mythical: true },
  { id: 493, name: "Arceus",     gen: 4, inGo: false, mythical: true, expectedRelease: "Late 2026" },

  // Gen 5
  { id: 495, name: "Snivy",      gen: 5, inGo: true },
  { id: 498, name: "Tepig",      gen: 5, inGo: true },
  { id: 501, name: "Oshawott",   gen: 5, inGo: true },
  { id: 570, name: "Zorua",      gen: 5, inGo: true },
  { id: 571, name: "Zoroark",    gen: 5, inGo: true },
  { id: 642, name: "Thundurus",  gen: 5, inGo: true, legendary: true },
  { id: 643, name: "Reshiram",   gen: 5, inGo: true, legendary: true },
  { id: 644, name: "Zekrom",     gen: 5, inGo: true, legendary: true },
  { id: 646, name: "Kyurem",     gen: 5, inGo: true, legendary: true },
  { id: 647, name: "Keldeo",     gen: 5, inGo: true, mythical: true },
  { id: 648, name: "Meloetta",   gen: 5, inGo: true, mythical: true },
  { id: 649, name: "Genesect",   gen: 5, inGo: true, mythical: true },

  // Gen 6
  { id: 650, name: "Chespin",    gen: 6, inGo: true },
  { id: 653, name: "Fennekin",   gen: 6, inGo: true },
  { id: 656, name: "Froakie",    gen: 6, inGo: true },
  { id: 700, name: "Sylveon",    gen: 6, inGo: true },
  { id: 716, name: "Xerneas",    gen: 6, inGo: true, legendary: true },
  { id: 717, name: "Yveltal",    gen: 6, inGo: true, legendary: true },
  { id: 718, name: "Zygarde",    gen: 6, inGo: false, legendary: true, expectedRelease: "Mid 2026" },
  { id: 719, name: "Diancie",    gen: 6, inGo: false, mythical: true, expectedRelease: "No date yet" },
  { id: 720, name: "Hoopa",      gen: 6, inGo: false, mythical: true, expectedRelease: "Late 2026" },
  { id: 721, name: "Volcanion",  gen: 6, inGo: false, mythical: true },

  // Gen 7
  { id: 722, name: "Rowlet",     gen: 7, inGo: true },
  { id: 725, name: "Litten",     gen: 7, inGo: true },
  { id: 728, name: "Popplio",    gen: 7, inGo: true },
  { id: 745, name: "Lycanroc",   gen: 7, inGo: true },
  { id: 746, name: "Wishiwashi", gen: 7, inGo: true },
  { id: 764, name: "Comfey",     gen: 7, inGo: true },
  { id: 771, name: "Pyukumuku",  gen: 7, inGo: true },
  { id: 778, name: "Mimikyu",    gen: 7, inGo: true },
  { id: 785, name: "Tapu Koko",  gen: 7, inGo: true, legendary: true },
  { id: 786, name: "Tapu Lele",  gen: 7, inGo: true, legendary: true },
  { id: 787, name: "Tapu Bulu",  gen: 7, inGo: true, legendary: true },
  { id: 788, name: "Tapu Fini",  gen: 7, inGo: true, legendary: true },
  { id: 789, name: "Cosmog",     gen: 7, inGo: true, legendary: true },
  { id: 791, name: "Solgaleo",   gen: 7, inGo: true, legendary: true },
  { id: 792, name: "Lunala",     gen: 7, inGo: true, legendary: true },
  { id: 793, name: "Nihilego",   gen: 7, inGo: true, ultraBeast: true },
  { id: 794, name: "Buzzwole",   gen: 7, inGo: true, ultraBeast: true },
  { id: 795, name: "Pheromosa",  gen: 7, inGo: true, ultraBeast: true },
  { id: 796, name: "Xurkitree",  gen: 7, inGo: true, ultraBeast: true },
  { id: 797, name: "Celesteela", gen: 7, inGo: true, ultraBeast: true },
  { id: 798, name: "Kartana",    gen: 7, inGo: true, ultraBeast: true },
  { id: 799, name: "Guzzlord",   gen: 7, inGo: true, ultraBeast: true },
  { id: 800, name: "Necrozma",   gen: 7, inGo: false, legendary: true },
  { id: 801, name: "Magearna",   gen: 7, inGo: true, mythical: true },
  { id: 802, name: "Marshadow",  gen: 7, inGo: false, mythical: true },
  { id: 803, name: "Poipole",    gen: 7, inGo: false, ultraBeast: true },
  { id: 805, name: "Stakataka",  gen: 7, inGo: true, ultraBeast: true },
  { id: 806, name: "Blacephalon",gen: 7, inGo: true, ultraBeast: true },
  { id: 807, name: "Zeraora",    gen: 7, inGo: true, mythical: true },

  // Gen 8
  { id: 810, name: "Grookey",    gen: 8, inGo: true },
  { id: 813, name: "Scorbunny",  gen: 8, inGo: true },
  { id: 816, name: "Sobble",     gen: 8, inGo: true },
  { id: 849, name: "Toxtricity", gen: 8, inGo: true },
  { id: 887, name: "Dragapult",  gen: 8, inGo: true },
  { id: 888, name: "Zacian",     gen: 8, inGo: true, legendary: true },
  { id: 889, name: "Zamazenta",  gen: 8, inGo: true, legendary: true },
  { id: 890, name: "Eternatus",  gen: 8, inGo: false, legendary: true, expectedRelease: "Coming soon" },
  { id: 891, name: "Kubfu",      gen: 8, inGo: false, legendary: true },
  { id: 892, name: "Urshifu",    gen: 8, inGo: false, legendary: true },
  { id: 893, name: "Zarude",     gen: 8, inGo: false, mythical: true },
  { id: 894, name: "Regieleki",  gen: 8, inGo: true, legendary: true },
  { id: 895, name: "Regidrago",  gen: 8, inGo: true, legendary: true },
  { id: 896, name: "Glastrier",  gen: 8, inGo: false, legendary: true },
  { id: 897, name: "Spectrier",  gen: 8, inGo: false, legendary: true },
  { id: 898, name: "Calyrex",    gen: 8, inGo: false, legendary: true },

  // Gen 9
  { id: 906, name: "Sprigatito", gen: 9, inGo: false, expectedRelease: "Coming late 2026" },
  { id: 909, name: "Fuecoco",    gen: 9, inGo: false, expectedRelease: "Coming late 2026" },
  { id: 912, name: "Quaxly",     gen: 9, inGo: false, expectedRelease: "Coming late 2026" },
  { id: 957, name: "Tinkatink",  gen: 9, inGo: true },
  { id: 958, name: "Tinkatuff",  gen: 9, inGo: true },
  { id: 959, name: "Tinkaton",   gen: 9, inGo: true },
  { id: 977, name: "Dondozo",    gen: 9, inGo: false },
  { id: 998, name: "Baxcalibur", gen: 9, inGo: false },
  { id: 1000,name: "Gholdengo",  gen: 9, inGo: false },
  { id: 1007,name: "Koraidon",   gen: 9, inGo: false, legendary: true, expectedRelease: "2027" },
  { id: 1008,name: "Miraidon",   gen: 9, inGo: false, legendary: true, expectedRelease: "2027" },
  { id: 1024,name: "Terapagos",  gen: 9, inGo: false, legendary: true, expectedRelease: "2027+" },
];

// Glyph URL helper, returns a data URI for the original Nexus Dex glyph for
// the given species. Drop-in replacement for the old PokeAPI sprite helper:
// existing <img src={spriteUrl(id)}> call sites now render Nexus glyphs.
//
// New components should import NexusGlyph from components/NexusGlyph.tsx so
// the SVG is inlined rather than embedded as a data URI.
export function spriteUrl(id: number, opts?: { shiny?: boolean; hq?: boolean }) {
  return nexusGlyphDataUri(id, { shiny: opts?.shiny });
}

export interface PokemonDexEntry {
  pokemon: Pokemon;
  status: DexStatus;
  isNew?: boolean;
}

export function findPokemon(id: number): Pokemon | undefined {
  return POKEMON.find((p) => p.id === id);
}
